import { Location } from '@angular/common';
import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { VideoHubService } from '@s/video-hub/video-hub.service';
import moment from 'moment-timezone';
import { Subject, Subscription, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { NavigationEnd, Router } from '@angular/router';
import { LoginService } from '@c1/login/login.service';
import {
  Broker,
  ChartTabs,
  JobStatus,
  ServerSettings,
  TabNames,
  loginUrl,
  releaseNotesUrl,
  securityAndPrivacyUrl,
  userAgreementUrl,
  videoHubUrl,
} from '@const';
import { BROKERS_CONFIG_MAP } from '@constants/brokers.constants';
import { environment } from '@env/environment';
import { PxoAiChatViewManagementService } from '@s/ai-chats/pxo-ai-chat-view-management.service';
import { RockyTasksService } from '@s/ai-chats/rocky-tasks.service';
import { BrokerAuthenticationService } from '@s/broker-authentication.service';
import { DialogsService } from '@s/common';
import { AuthService } from '@s/common/auth.service';
import { CookieService } from '@s/cookie.service';
import { FeedbackButtonService } from '@s/feedback-button.service';
import { IframeMessageService } from '@s/iframe-message.service';
import { JobDataService } from '@s/job-data.service';
import { NavigationService } from '@s/navigation.service';
import { ObservableService } from '@s/observable.service';
import { PwaService } from '@s/pwa.service';
import { ProcessingScreenConfiguration, ServerDataService } from '@s/server-data.service';
import { UserDataService } from '@s/user-data.service';
import { getTabNameByRoute, getUrlSegmentsByRoute } from '@u/utils';
import { ObservableService as ObservableServiceV1 } from '../app/core/directives/observable.service';

/** Session key to track if Rocky was already opened in this PWA session */
const PWA_ROCKY_OPENED_KEY = 'pwa_rocky_opened_this_session';

const HIDE_PRELOAD_IMAGE_DELAY_MS = 2000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterContentInit, OnDestroy {
  public title = 'angular-pxo';

  protected showImpersonateBar = false;
  protected impersonateUserName = '';
  protected impersonateUserEmail = '';

  protected isNeedPrint$ = this._observableService.isNeedPrint$;

  private subscriptions = new Subscription();

  private processingScreenConfigurations: Array<ProcessingScreenConfiguration> = [];
  private numberCrunchingStartSubscription: Subscription;
  private numberCrunchingSubscription: Subscription;

  protected tags;

  private _destroy$: Subject<boolean> = new Subject();

  constructor(
    private _observableService: ObservableService,
    private _observableServiceV1: ObservableServiceV1,
    private _userDataService: UserDataService,
    private _authService: AuthService,
    private _jobDataService: JobDataService,
    private _serverDataService: ServerDataService,
    private _brokerAuthenticationService: BrokerAuthenticationService,
    private _loginService: LoginService,
    private _cookieService: CookieService,
    private navigationService: NavigationService,
    private location: Location,
    private videoHubService: VideoHubService,
    private iframeMessageService: IframeMessageService,
    private router: Router,
    private pwaService: PwaService,
    private pxoAiChatViewManagementService: PxoAiChatViewManagementService,
    private rockyTasksService: RockyTasksService,
    private feedbackButtonService: FeedbackButtonService,
    private dialogsService: DialogsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this._authService.clearStorageOnRefresh();

    const isReleaseNotesRoute = window.location.hash.includes('release-notes');
    if (isReleaseNotesRoute) {
      this.hidePreloadImage();

      return;
    }

    // Public, unauthenticated access to /security-and-privacy must skip all
    // authenticated-app initialization (no redirectToTab, no token-expiration
    // interval, no auth-required services).
    const isSecurityAndPrivacyRoute = window.location.href.includes(securityAndPrivacyUrl);
    if (isSecurityAndPrivacyRoute && !this._authService.isTokenValid()) {
      this.hidePreloadImage();

      return;
    }

    const params = new URLSearchParams(window.location.search);
    const brokerLoginState = params.get('state');
    const brokerLoginCode = params.get('code');
    const connectionId = params.get('connection_id');

    const broker = this._cookieService.get(BrokerAuthenticationService.brokerCookieName);
    const isSandbox = this._cookieService.get(BrokerAuthenticationService.isSandboxCookieName) === '1';

    if ((brokerLoginState && brokerLoginCode) || connectionId) {
      if (broker === Broker.Tradier) {
        await this._brokerAuthenticationService.mintAccessTokenTradier(brokerLoginState, brokerLoginCode, isSandbox);
      } else if (broker === Broker.TradeZero) {
        await this._brokerAuthenticationService.mintAccessTokenTradeZero(brokerLoginState, brokerLoginCode, isSandbox);
      } else if (
        BROKERS_CONFIG_MAP.has(broker as Broker) &&
        broker !== Broker.Tradier &&
        broker !== Broker.TradeZero &&
        (BROKERS_CONFIG_MAP.get(broker as Broker)?.isVisible !== false ||
          this._observableService.isDeveloperAccount.getValue()) &&
        connectionId
      ) {
        await this._brokerAuthenticationService.mintAccessTokenSnapTrade(broker as Broker, connectionId);
      }

      await this.navigationService.redirectToTab(TabNames.TradingPanel);
    } else {
      const currentLocation = this.location.path(false).split('?')[0];

      if (
        !currentLocation.includes(loginUrl) &&
        !currentLocation.includes('ui-templates') &&
        !currentLocation.includes(videoHubUrl) &&
        !currentLocation.includes(securityAndPrivacyUrl)
      ) {
        const alertPreviewNotificationId = AppComponent.getAlertPreviewDeepLinkNotificationId();
        const rockyTaskDeepLinkTurnId = AppComponent.getRockyTaskDeepLinkTurnId();
        const rockyTaskDisabledDeepLinkTurnId = AppComponent.getRockyTaskDisabledDeepLinkTurnId();
        const rockyTaskFailedDeepLink = AppComponent.getRockyTaskFailedDeepLink();
        if (alertPreviewNotificationId !== null) {
          this.dialogsService.preloadAlertPreviewDeepLink(alertPreviewNotificationId);
        }
        if (rockyTaskDisabledDeepLinkTurnId !== null) {
          this.dialogsService.preloadRockyTaskPaused(rockyTaskDisabledDeepLinkTurnId);
        }
        if (rockyTaskFailedDeepLink !== null) {
          this.dialogsService.preloadRockyTaskFailed(rockyTaskFailedDeepLink);
        }

        // sync activeTab and URL state (URL could be changed in app-loader)
        const initialParams = this._observableService.initialQueryParams$.getValue();
        const securityIdToApply = initialParams?.security_id ?? null;
        const activeTab = this._observableService.activeTab.getValue();

        const tabsRequiringChartState = [TabNames.Wheel, TabNames.Wtf, TabNames.DividendsStrategy];
        const stateToPass = {
          ...initialParams,
          ...(securityIdToApply && tabsRequiringChartState.includes(activeTab) ? { tab: ChartTabs.Chart } : {}),
        };

        await this.navigationService.redirectToTab(activeTab, stateToPass, securityIdToApply);

        if (alertPreviewNotificationId !== null) {
          this.dialogsService.openAlertPreviewDeepLinkModal(alertPreviewNotificationId);
        }

        if (rockyTaskDisabledDeepLinkTurnId !== null) {
          this.dialogsService.openRockyTaskPausedModal(rockyTaskDisabledDeepLinkTurnId);
        }

        if (rockyTaskFailedDeepLink !== null) {
          this.dialogsService.openRockyTaskFailedModal(rockyTaskFailedDeepLink);
        }

        if (rockyTaskDeepLinkTurnId !== null) {
          await this.openRockyTaskDeepLink(rockyTaskDeepLinkTurnId);
        }
      }
    }

    interval(environment.TokenExpirationCheckIntervalMS)
      .pipe(
        takeUntil(this._destroy$),
        filter(() => {
          const currentUrl = window.location.href;
          const isPublicSecurityAndPrivacyView =
            currentUrl.indexOf(securityAndPrivacyUrl) !== -1 && !this._authService.isTokenValid();

          return (
            currentUrl.indexOf(loginUrl) === -1 &&
            currentUrl.indexOf(userAgreementUrl) === -1 &&
            !isPublicSecurityAndPrivacyView
          );
        }),
        filter(() => !this._authService.isTokenValid()),
      )
      .subscribe(() => {
        this.iframeMessageService.notifySessionEnd();
        this._authService.logout(true);
      });

    this.subscriptions.add(
      this._observableServiceV1.userSetting.subscribe(
        (settings) => (this.tags = settings.tags?.join(',') || this._userDataService.getUser()?.tags?.join(',') || ''),
      ),
    );

    this._authService.watchUserActivity(environment.UpdateTokenByUserActivityDelaySec);

    this.processingScreenConfigurations = await this._serverDataService.getAsObject(
      ServerSettings.DataProcessingScreenConfiguration,
    );

    // Start polling server every 1 minute to see if the processing has already finished
    this.subscriptions.add(
      this._observableService.numberCrunchingCountDownTimerFinished.subscribe(
        async () => await this.getNumberCrunchingStatus(),
      ),
    );

    await this.initializeNumberCrunching();

    const impersonateInfo = this._loginService.getImpersonateDetails();
    this.showImpersonateBar = impersonateInfo !== null;
    this.impersonateUserEmail = impersonateInfo?.email;
    this.impersonateUserName = impersonateInfo?.userName;

    this.videoHubService.openBackgroundVideoFromStorage();

    // Open Rocky chat if running as PWA and logged in (once per session)
    this.openRockyForPwaIfNeeded();

    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const urlSegments = getUrlSegmentsByRoute(event.urlAfterRedirects);
          const tabName = getTabNameByRoute(urlSegments);
          if (tabName) {
            this.navigationService.setActiveTab(tabName);
          }
          if (event.urlAfterRedirects.includes(releaseNotesUrl)) {
            this.hidePreloadImage();
          }
        }
      }),
    );
  }

  ngAfterContentInit(): void {
    const isReleaseNotes =
      this.location.path().includes(releaseNotesUrl) || window.location.hash.includes('release-notes');
    const delayMs = isReleaseNotes ? 0 : HIDE_PRELOAD_IMAGE_DELAY_MS;
    setTimeout(() => this.hidePreloadImage(), delayMs);
  }

  private hidePreloadImage(): void {
    const preloadImg = document.querySelector('.preload-img') as HTMLElement;

    if (preloadImg) {
      preloadImg.style.opacity = '0';
      preloadImg.style.zIndex = '-10';
    }
  }

  ngOnDestroy(): void {
    this.cleanupNumberCrunchingStartSubscription();
    this.cleanupNumberCrunchingSubscription();

    this.feedbackButtonService.destroy();
    this.subscriptions.unsubscribe();
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  /**
   * Opens Rocky chat if running as PWA and user is logged in.
   * Only opens once per session to avoid repeated popups.
   */
  private openRockyForPwaIfNeeded(): void {
    // Only run if in standalone PWA mode
    if (!this.pwaService.isStandalone) {
      return;
    }

    // Check if user is logged in
    const userDataString = localStorage.getItem('user');
    const isLoggedIn = !!userDataString;
    if (!isLoggedIn) {
      return;
    }

    // Check if we already opened Rocky this session
    if (sessionStorage.getItem(PWA_ROCKY_OPENED_KEY)) {
      return;
    }

    // Mark as opened for this session
    sessionStorage.setItem(PWA_ROCKY_OPENED_KEY, 'true');

    // Open Rocky chat
    this.pxoAiChatViewManagementService.showBackgroundWindow();
  }

  async initializeNumberCrunching(): Promise<void> {
    const startNumberCrunching = await this.startNumberCrunching();
    // check overtime processing
    if (!startNumberCrunching) {
      await this.getNumberCrunchingStatus();
    }
  }

  async startNumberCrunching(): Promise<boolean> {
    for (const processingConfiguration of this.processingScreenConfigurations) {
      const timeNow = moment.tz(processingConfiguration.timeZone);

      // check if server polling or processing day
      if (!processingConfiguration.processingDays.includes(timeNow.isoWeekday())) {
        continue;
      }

      const startTime = moment
        .tz(processingConfiguration.timeZone)
        .startOf('day')
        .add(processingConfiguration.startTime);
      const expectedFinishTime = startTime.clone().add(processingConfiguration.processingMinutes, 'minutes');

      if (timeNow.unix() >= startTime.unix() && timeNow.unix() < expectedFinishTime.unix()) {
        this.cleanupNumberCrunchingStartSubscription();

        // expected time hasn't passed - show countdown timer
        if (timeNow.unix() < expectedFinishTime.unix()) {
          this._observableService.showMaintenance.next(true);
          this._observableService.startNumberCrunchingTime.next(startTime.toDate().getTime());
          this._observableService.finishNumberCrunchingTime.next(expectedFinishTime.toDate().getTime());

          this._observableService.numberCrunchingStatus.next(JobStatus.InProgress);
        } else {
          await this.getNumberCrunchingStatus();
        }

        return true;
      }
    }

    return false;
  }

  async getNumberCrunchingStatus(): Promise<void> {
    const schedulers = await this._jobDataService.getExchangesDailySchedulers([JobStatus.New, JobStatus.InProgress]);

    if (!schedulers.length) {
      if (this._observableService.showMaintenance.getValue()) {
        // this._observableService.showMaintenance.next(false);
        this._observableService.numberCrunchingStatus.next(JobStatus.Done);
      }
      this.cleanupNumberCrunchingSubscription();
      this.setupNumberCrunchingStartSubscription();
      return;
    }

    this._observableService.showMaintenance.next(true);
    this._observableService.numberCrunchingStatus.next(JobStatus.Overtime);

    // poll every minute to check for updates
    if (!this.numberCrunchingSubscription) {
      this.numberCrunchingSubscription = interval(1000 * 60).subscribe(async () => {
        await this.getNumberCrunchingStatus();
      });
    }
  }

  setupNumberCrunchingStartSubscription(): void {
    // Check every second if it is time to show maintenance page
    this.numberCrunchingStartSubscription = interval(1000).subscribe(async () => {
      await this.startNumberCrunching();
    });
  }

  cleanupNumberCrunchingStartSubscription(): void {
    if (this.numberCrunchingStartSubscription) {
      this.numberCrunchingStartSubscription.unsubscribe();
    }
  }

  cleanupNumberCrunchingSubscription(): void {
    if (this.numberCrunchingSubscription) {
      this.numberCrunchingSubscription.unsubscribe();
    }
  }

  protected logout(): void {
    this.iframeMessageService.notifySessionEnd();
    this._authService.logout();
  }

  private static getAlertPreviewDeepLinkNotificationId(): number | null {
    const match = window.location.hash.match(/^#(?:\/)?alert-preview(?:\?(.*))?$/);
    if (!match) {
      return null;
    }

    const params = new URLSearchParams(match[1] ?? '');
    const rawNotificationId = params.get('n_id');
    if (!rawNotificationId) {
      return null;
    }

    const notificationId = Number(rawNotificationId);
    return Number.isInteger(notificationId) && notificationId > 0 ? notificationId : null;
  }

  private static getRockyTaskDeepLinkTurnId(): string | null {
    const match = window.location.hash.match(/^#(?:\/)?rocky-task(?:\?(.*))?$/);
    if (!match) {
      return null;
    }

    const params = new URLSearchParams(match[1] ?? '');
    const turnId = params.get('i_id')?.trim();

    return turnId || null;
  }

  private static getRockyTaskDisabledDeepLinkTurnId(): string | null {
    const match = window.location.hash.match(/^#(?:\/)?rocky-task-disabled(?:\?(.*))?$/);
    if (!match) {
      return null;
    }

    const params = new URLSearchParams(match[1] ?? '');
    const turnId = params.get('i_id')?.trim();

    return turnId || null;
  }

  private static getRockyTaskFailedDeepLink(): string | null {
    const match = window.location.hash.match(/^#(?:\/)?rocky-task-failed(?:\?(.*))?$/);
    if (!match) {
      return null;
    }

    const params = new URLSearchParams(match[1] ?? '');
    const turnId = params.get('i_id')?.trim();

    return turnId || null;
  }

  private async openRockyTaskDeepLink(turnId: string): Promise<void> {
    await this.rockyTasksService.openExecutionResultInChat(turnId);
  }
}
